import { ref, computed, onBeforeUnmount } from 'vue';
import { parseDraftOutput, stripDashes, draftFailureMessage } from './rationaleDraft';

/**
 * Rationale Draft runner (PD-1301) — shared between rm-portal and rm-portal-beta.
 *
 * Fires the `rationale_draft` prompt chain ONCE for one question (a sub-factor or
 * a classification factor) and exposes the result for the rationale editor. The
 * chain's manual_values are built by the CALLER (see rationaleDraft.buildDraftInputs),
 * because only the caller knows the factor, level and evidence in play.
 *
 * Evidence dependency: the draft is grounded on the AI-assist chain that has
 * already run for the unit. That chain may still be executing when the
 * evaluator clicks, so `run` waits for it inside the same "generating" state
 * rather than surfacing a second waiting state — the design has one.
 *
 * States: 'idle' | 'generating' | 'done' | 'failed'. On failure `errorKind` says
 * why: 'evidence' (assist never produced usable evidence in time), 'insufficient'
 * (the model declined to fabricate), 'chain' (trigger/poll/parse failure).
 *
 * The prompt-chain client and the polling service are INJECTED because they live
 * at portal-local paths — same reason docReadinessRun does it.
 *
 * @param {Object} deps
 * @param {{triggerChain:Function,getExecutionStatus:Function}} deps.promptChainApi
 * @param {{startPolling:Function,stopPolling:Function}}        deps.executionPolling
 */
export function rationaleDraftRun({ promptChainApi, executionPolling }) {
  const self = {};

  self.state = ref('idle');
  self.draft = ref('');
  self.error = ref(null);
  self.errorKind = ref(null);
  self.citedIds = ref([]);
  self.executionId = ref(null);

  self.generating = computed(() => self.state.value === 'generating');

  let _token = 0;
  let _activeExecutionId = null;
  let _lastRequest = null;

  function _fail(kind, message) {
    self.state.value = 'failed';
    self.errorKind.value = kind;
    self.error.value = message;
  }

  function _collectOutputs(execution) {
    const all = {};
    (execution.steps || []).forEach((step) => {
      if (!step.outputs) return;
      for (const [key, value] of Object.entries(step.outputs)) {
        if (typeof value === 'string') {
          try {
            all[key] = JSON.parse(value);
          } catch {
            all[key] = value;
          }
        } else {
          all[key] = value;
        }
      }
    });
    return all;
  }

  // Resolve when the assist evidence for the unit is ready, or give up.
  // `getEvidence()` → { ready, failed, items }. `ensureEvidence()` kicks the
  // assist chain off if the evaluator never opened the panel for this unit.
  function _awaitEvidence({ getEvidence, ensureEvidence, waitForEvidenceMs }, token) {
    return new Promise((resolve) => {
      const started = Date.now();
      if (ensureEvidence) {
        try { ensureEvidence(); } catch { /* the assist composable reports its own errors */ }
      }
      const tick = () => {
        if (token !== _token) return resolve({ items: null, reason: 'superseded' });
        const ev = getEvidence ? getEvidence() : { ready: true, items: [] };
        if (ev && ev.failed) return resolve({ items: null, reason: 'failed' });
        if (ev && ev.ready) return resolve({ items: ev.items || [], reason: null });
        if (Date.now() - started >= waitForEvidenceMs) return resolve({ items: null, reason: 'timeout' });
        setTimeout(tick, 500);
      };
      tick();
    });
  }

  function _trigger(request, manualValues, token) {
    return new Promise((resolve) => {
      const payload = {
        prompt_chain_id: request.chainId,
        source_data: {
          custom: { system_reference: request.systemReference, ...(request.sourceCustom || {}) },
        },
        manual_values: manualValues,
      };

      promptChainApi
        .triggerChain(payload)
        .then((resp) => {
          if (!resp.data || !resp.data.success) {
            resolve({ ok: false, message: (resp.data && resp.data.error) || 'Failed to start chain execution' });
            return;
          }
          const executionId = resp.data.execution_id;
          _activeExecutionId = executionId;
          if (token === _token) self.executionId.value = executionId;

          executionPolling.startPolling(
            executionId,
            async () => {
              const statusResp = await promptChainApi.getExecutionStatus(executionId);
              if (!statusResp.data || !statusResp.data.success) return {};
              const execution = statusResp.data.data;
              switch (execution.execution_status) {
                case 'queued':
                case 'running':
                  return {};
                case 'completed':
                  return { shouldStop: true, data: { outputs: _collectOutputs(execution) } };
                case 'failed':
                  return { shouldStop: true, data: { failed: execution.error_message || 'Execution failed' } };
                default:
                  return {};
              }
            },
            (data) => {
              _activeExecutionId = null;
              if (data && data.failed) resolve({ ok: false, message: data.failed });
              else resolve({ ok: true, outputs: (data && data.outputs) || null });
            },
            () => {
              _activeExecutionId = null;
              resolve({ ok: false, message: 'Lost connection while polling for status' });
            },
          );
        })
        .catch((err) => {
          resolve({ ok: false, message: (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || 'Failed to execute chain' });
        });
    });
  }

  /**
   * @param {Object}   request
   * @param {number}   request.chainId            rationale_draft chain id
   * @param {string}   request.systemReference    doc.system_number, for execution lookup/audit
   * @param {Object}   [request.sourceCustom]     extra source_data.custom keys (factor_id, sub_factor_id, level)
   * @param {Function} request.buildInputs        (evidenceItems) => manual_values
   * @param {Function} [request.getEvidence]      () => { ready, failed, items }
   * @param {Function} [request.ensureEvidence]   () => void — start the assist chain for the unit if needed
   * @param {number}   [request.waitForEvidenceMs] default 45s
   */
  self.run = async (request) => {
    if (!request || !request.chainId) {
      _fail('chain', 'Atlas is not available here.');
      return;
    }
    _lastRequest = request;
    const token = ++_token;
    if (_activeExecutionId) {
      executionPolling.stopPolling(_activeExecutionId);
      _activeExecutionId = null;
    }
    self.state.value = 'generating';
    self.error.value = null;
    self.errorKind.value = null;
    self.executionId.value = null;

    const waitMs = Number(request.waitForEvidenceMs) > 0 ? Number(request.waitForEvidenceMs) : 45000;
    const evidence = await _awaitEvidence({ ...request, waitForEvidenceMs: waitMs }, token);
    if (token !== _token) return;
    if (evidence.reason === 'superseded') return;
    if (evidence.reason) {
      _fail('evidence', 'Atlas could not read the document evidence for this question.');
      return;
    }

    let manualValues;
    try {
      // `system_reference` is a body field of the draft endpoint as well as the
      // execution's audit key, so it rides in the inputs too.
      manualValues = { system_reference: request.systemReference, ...(request.buildInputs ? request.buildInputs(evidence.items) : {}) };
    } catch {
      _fail('chain', 'Atlas could not draft this one.');
      return;
    }

    const result = await _trigger(request, manualValues, token);
    if (token !== _token) return;
    if (!result.ok) {
      _fail('chain', 'Atlas could not draft this one.');
      return;
    }

    const parsed = parseDraftOutput(result.outputs);
    if (!parsed) {
      _fail('chain', 'Atlas could not draft this one.');
      return;
    }
    const text = stripDashes(parsed.rationale);
    if (parsed.insufficient || !text) {
      // The service's own withheld message is not shown: it is written for a
      // reader of the API, and the design has one error line with Try again.
      _fail('insufficient', draftFailureMessage(parsed.reasons));
      return;
    }
    self.draft.value = text;
    self.citedIds.value = parsed.citedIds;
    self.state.value = 'done';
  };

  self.retry = () => (_lastRequest ? self.run(_lastRequest) : Promise.resolve());

  self.reset = () => {
    _token++;
    if (_activeExecutionId) {
      executionPolling.stopPolling(_activeExecutionId);
      _activeExecutionId = null;
    }
    self.state.value = 'idle';
    self.draft.value = '';
    self.error.value = null;
    self.errorKind.value = null;
    self.citedIds.value = [];
    self.executionId.value = null;
  };

  onBeforeUnmount(() => self.reset());

  return self;
}
